import { useEffect, useState } from 'preact/hooks';
import { BackLink } from '../components/BackLink';
import { PlaceholderScreen } from './PlaceholderScreen';
import { getSource, fetchSourceOriginal, ApiError, type SourceDoc, type SourceOriginal } from '../lib/api';
import './SourceViewer.css';

export interface SourceViewerProps {
  tenantId: string;
  id: string;
  onBack: () => void;
}

type LoadState = 'loading' | 'ready' | 'error';

const KIND_LABEL: Record<string, string> = {
  email: 'Email',
  call_sheet: 'Call sheet',
  callsheet: 'Call sheet',
  contract: 'Contract',
  deal_memo: 'Deal memo',
  sides: 'Sides',
  breakdown: 'Breakdown',
  script: 'Script',
};

function formatReceived(iso: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// B6b (Huan 2026-07-13 correction): the source IS the ORIGINAL file. This
// viewer opens the retained call-sheet PDF/image in its original form (an
// <iframe> for a PDF, an <img> for an image), served by GET
// /api/actor/sources/:id/original. The extracted raw_text is DEMOTED to a
// clearly-secondary, collapsible "our reading" section BELOW the original —
// the file above is the source of truth. Reached via `.srclink` chips from the
// audition breakdown and the booked project's day-of card.
export function SourceViewer({ tenantId, id, onBack }: SourceViewerProps) {
  const [state, setState] = useState<LoadState>('loading');
  const [source, setSource] = useState<SourceDoc | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // The retained original (object URL + mime). Fetched only when the source
  // actually has a stored file (media_ref); null otherwise.
  const [original, setOriginal] = useState<SourceOriginal | null>(null);
  const [originalError, setOriginalError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let revoke: string | null = null;
    async function load() {
      setState('loading');
      setOriginal(null);
      setOriginalError(null);
      try {
        const data = await getSource(tenantId, id);
        if (cancelled) return;
        setSource(data);
        setState('ready');
        if (data.media_ref) {
          try {
            const orig = await fetchSourceOriginal(tenantId, id);
            if (cancelled) {
              URL.revokeObjectURL(orig.objectUrl);
              return;
            }
            revoke = orig.objectUrl;
            setOriginal(orig);
          } catch (err) {
            if (cancelled) return;
            setOriginalError(err instanceof ApiError ? err.message : 'Could not load the original file.');
          }
        }
      } catch (err) {
        if (cancelled) return;
        setErrorMessage(err instanceof ApiError ? err.message : 'Could not reach the server.');
        setState('error');
      }
    }
    void load();
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [tenantId, id]);

  if (state === 'loading') {
    return (
      <div class="source-viewer">
        <BackLink label="back" onClick={onBack} />
        <PlaceholderScreen eyebrow="Source" line="One moment…" />
      </div>
    );
  }

  if (state === 'error' || !source) {
    return (
      <div class="source-viewer">
        <BackLink label="back" onClick={onBack} />
        <PlaceholderScreen eyebrow="Source" line="Couldn't load this source." soft={errorMessage ?? undefined} />
      </div>
    );
  }

  const kind = source.kind ? (KIND_LABEL[source.kind] ?? source.kind) : null;
  const filename = source.filename;
  const received = formatReceived(source.received_at);
  const title = filename ?? kind ?? 'Original source';
  const metaParts = [kind, filename && filename !== title ? filename : null, received].filter(Boolean);
  const rawText = source.raw_text;
  const hasOriginal = Boolean(source.media_ref);
  const downloadName = filename ?? 'original';
  const isImage = original?.mimeType.startsWith('image/') ?? false;

  return (
    <div class="source-viewer">
      <BackLink label="back" onClick={onBack} />
      <div class="source-viewer__title">{title}</div>
      {metaParts.length > 0 && <div class="source-viewer__meta">{metaParts.join(' · ')}</div>}

      {hasOriginal ? (
        <>
          <div class="source-viewer__note">The original file, exactly as it was sent — this is the source of truth.</div>
          {original ? (
            <>
              <div class="srcfile">
                {isImage ? (
                  <img class="srcfile__img" src={original.objectUrl} alt={downloadName} />
                ) : (
                  <iframe class="srcfile__frame" src={original.objectUrl} title={downloadName} />
                )}
              </div>
              <div class="srcfile__actions">
                <a class="srcfile__open" href={original.objectUrl} target="_blank" rel="noopener noreferrer">
                  Open original
                </a>
                <a class="srcfile__download" href={original.objectUrl} download={downloadName}>
                  Download
                </a>
              </div>
            </>
          ) : originalError ? (
            <div class="source-viewer__empty">{originalError}</div>
          ) : (
            <div class="source-viewer__empty">Loading the original file…</div>
          )}
        </>
      ) : (
        <div class="source-viewer__note">
          No original file was retained for this source — only the reading below was captured.
        </div>
      )}

      {rawText && (
        <details class="srcreading" open={!hasOriginal}>
          <summary class="srcreading__summary">Our reading (text extraction)</summary>
          <div class="srcreading__caption">
            {hasOriginal
              ? 'What we pulled out to fill the screens — the file above is the source of truth.'
              : 'What we pulled out to fill the screens.'}
          </div>
          <div class="srcdoc">{rawText}</div>
        </details>
      )}

      {!rawText && !hasOriginal && (
        <div class="source-viewer__empty">No text captured for this source.</div>
      )}
    </div>
  );
}
